import React, { useState, useEffect } from 'react';
import { PageHeader, Card, Input, Button, Loading } from '../components/ui';
import { useApi } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';

interface SiteSettings {
  site_name: string;
  footer_text: string;
}

const SettingsPage: React.FC = () => {
  const { data, loading, fetch: fetchSettings, update } = useApi<SiteSettings>('/api/content/settings');
  const { user } = useAuth();
  const { showToast } = useToast();
  const [form, setForm] = useState<SiteSettings>({
    site_name: '',
    footer_text: '',
  });
  const [saving, setSaving] = useState(false);

  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  useEffect(() => {
    if (data) {
      setForm({
        site_name: data.site_name || '',
        footer_text: data.footer_text || '',
      });
    }
  }, [data]);

  const handleSave = async () => {
    setSaving(true);
    const result = await update(form);
    setSaving(false);
    if (result) {
      showToast('Settings saved!', 'success');
    } else {
      showToast('Failed to save settings', 'error');
    }
  };

  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword) {
      showToast('Please fill in all password fields', 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('New passwords do not match', 'error');
      return;
    }
    if (newPassword.length < 8) {
      showToast('Password must be at least 8 characters', 'error');
      return;
    }

    setChangingPassword(true);
    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json();

      if (response.ok) {
        showToast('Password changed successfully!', 'success');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        showToast(data.error || 'Failed to change password', 'error');
      }
    } catch {
      showToast('Network error', 'error');
    }
    setChangingPassword(false);
  };

  if (loading && !data) return <Loading />;

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="Global site settings and account management"
        actions={<Button onClick={handleSave} loading={saving}>Save Settings</Button>}
      />

      <div className="admin-content">
        <Card title="Site Settings">
          <div className="admin-form">
            <Input
              label="Site Name"
              value={form.site_name}
              onChange={e => setForm(f => ({ ...f, site_name: e.target.value }))}
              placeholder="andrxw"
            />
            <Input
              label="Footer Text"
              value={form.footer_text}
              onChange={e => setForm(f => ({ ...f, footer_text: e.target.value }))}
              placeholder="All rights reserved."
            />
          </div>
        </Card>

        <div style={{ marginTop: 24 }}>
          <Card title="Account">
            <div style={{ marginBottom: 20, padding: 16, background: 'var(--admin-bg)', borderRadius: 8 }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--admin-text-muted)' }}>Logged in as</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 500, marginTop: 4 }}>{user?.email}</div>
            </div>

            <h4 style={{ marginBottom: 16, fontSize: '0.95rem' }}>Change Password</h4>
            <div className="admin-form">
              <Input
                label="Current Password"
                type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                placeholder="••••••••"
              />
              <Input
                label="New Password"
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="••••••••"
                hint="Minimum 8 characters"
              />
              <Input
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
              />
              <Button onClick={handlePasswordChange} loading={changingPassword} variant="secondary">
                Change Password
              </Button>
            </div>
          </Card>
        </div>

        <div style={{ marginTop: 24 }}>
          <Card title="Database">
            <p style={{ color: 'var(--admin-text-muted)', marginBottom: 16 }}>
              Initialize or reset the database tables. This is typically only needed once during setup.
            </p>
            <Button
              variant="secondary"
              onClick={async () => {
                if (!window.confirm('Initialize database? This will create tables and seed default data if empty.')) return;
                try {
                  const response = await fetch('/api/init-db', { credentials: 'include' });
                  if (response.ok) {
                    showToast('Database initialized!', 'success');
                  } else {
                    showToast('Failed to initialize database', 'error');
                  }
                } catch {
                  showToast('Network error', 'error');
                }
              }}
            >
              Initialize Database
            </Button>
          </Card>
        </div>
      </div>
    </>
  );
};

export default SettingsPage;
